import { useTranslation } from 'react-i18next';
import { Header } from '@/components/header';
import { Partners } from '@/components/partners';
import { Footer } from '@/components/footer';
import CTA from '@/components/cta';
import { FAQ, FAQItemType } from '@/components/faq';

export default function GatewayPage() {
  const { t } = useTranslation();

  const faqs: FAQItemType[] = [
    { id: 'q1', question: t('gateway.faq.q1.question'), answer: t('gateway.faq.q1.answer') },
    { id: 'q2', question: t('gateway.faq.q2.question'), answer: t('gateway.faq.q2.answer') },
    { id: 'q3', question: t('gateway.faq.q3.question'), answer: t('gateway.faq.q3.answer') },
    { id: 'q4', question: t('gateway.faq.q4.question'), answer: t('gateway.faq.q4.answer') },
    { id: 'q5', question: t('gateway.faq.q5.question'), answer: t('gateway.faq.q5.answer') },
  ];

  const whyItems = [
    { title: t('gateway.whyChoose.subtitle1'), desc: t('gateway.whyChoose.description1'), image: '/gateway/one plug.png' },
    { title: t('gateway.whyChoose.subtitle2'), desc: t('gateway.whyChoose.description2'), image: '/gateway/security u can trust.png' },
    { title: t('gateway.whyChoose.subtitle3'), desc: t('gateway.whyChoose.description3'), image: '/gateway/unlimited device gateway.png' },
    { title: t('gateway.whyChoose.subtitle4'), desc: t('gateway.whyChoose.description4'), image: '/gateway/always connected.png' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />

      {/* Hero / Intro with background */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="/gateway/Gateway-Banner.png"
            alt="Oleyes Gateway"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Foreground content */}
        <div className="container mx-auto relative z-10 min-h-[60vh] md:min-h-[70vh] lg:min-h-[90vh] flex items-center justify-start">
          <div className="max-w-xl md:max-w-2xl text-left">
            <h1 className="text-xl md:text-5xl lg:text-6xl font-extrabold text-white mb-12 leading-tight uppercase tracking-tight">{t('gateway.title')}</h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl italic mx-auto">"{t('gateway.subtitle')}"</p>
            <p className="text-lg md:text-xl leading-8 text-slate-200 text-justify">
              {t('gateway.description')}
            </p>
          </div>
        </div>

        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 opacity-40">
              <button 
                onClick={() => {
                  window.scrollTo({
                    top: window.innerHeight,
                    behavior: 'smooth'
                  });
                }}
                aria-label="Scroll down"
                className="flex flex-col items-center text-white hover:text-primary-200 transition-colors group"
              >
                <span className="text-sm font-medium mb-2 opacity-80 group-hover:opacity-100 transition-opacity">
                  {t('gateway.scrollDown')}
                </span>
                <div className="w-10 h-16 rounded-full border-2 border-white/50 flex items-start justify-center p-1 group-hover:border-white transition-colors">
                  <div className="w-1 h-4 bg-white rounded-full animate-bounce" />
                </div>
              </button>
            </div>
      </section>


      {/* Why Choose - Alternating timeline with image placeholders */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
              {t('gateway.whyChoose.title')}
            </h2>
          </div>

          <div className="max-w-6xl mx-auto space-y-16">
            {whyItems.map((item, idx) => (
              <div key={idx} className="md:grid md:grid-cols-12 md:items-center gap-8">
                {/* Left/Right swap */}
                {(idx % 2 === 0) ? (
                  <>
                    <div className="md:col-span-5">
                      <div className="relative aspect-[16/9] rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-7 mt-6 md:mt-0">
                      <div className="flex items-start gap-4">
                        <div className="relative">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-semibold">
                            {(idx + 1).toString().padStart(2, '0')}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{item.title}</h3>
                          <p className="mt-2 text-slate-600 dark:text-slate-300">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="md:col-span-7 order-2 md:order-1 mt-6 md:mt-0">
                      <div className="flex items-start gap-4 md:justify-end">
                        <div>
                          <h3 className="text-xl font-semibold text-slate-900 dark:text-white text-left md:text-right">{item.title}</h3>
                          <p className="mt-2 text-slate-600 dark:text-slate-300 text-left md:text-right">{item.desc}</p>
                        </div>
                        <div className="relative">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-semibold">
                            {(idx + 1).toString().padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-5 order-1 md:order-2">
                      <div className="relative aspect-[16/9] rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <FAQ
            faqs={faqs}
            title={t('gateway.faq.title')}
            subtitle={t('gateway.faq.subtitle')}
            className=""
          />
        </div>
      </section>

      <CTA />
      <Partners />
      <Footer />
    </div>
  );
}
